import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getSyncedTimeMs } from '@/lib/timeSync';

export interface User {
  id: string;
  whiteLabelId?: string;
  b2bId?: string;
  b2cId?: string;
  username: string;
  name: string;
  email: string;
  userType: string;
  agentPrefix?: string;
  roleId?: string;
  isWlAdmin?: boolean;
  isB2BAdmin?: boolean;
  isB2CAdmin?: boolean;
  isSystemAdmin?: boolean;
  selectedCurrency?: string;
  credit?: number;
  limit?: number;
  sidebar_fav?: string[];
  table_fav?: { type: string; id: string }[];
  activeMembershipId?: string;
  memberships?: UserAgentMembership[];
}

export interface UserAgentMembership {
  id: string;
  userType: string;
  whiteLabelId?: string;
  b2bId?: string;
  b2cId?: string;
  roleId?: string;
  status: string;
  isDefault: boolean;
  agentPrefix: string;
  agentDesc?: string;
  imageLogo?: string;
}

export interface UserRole {
  id: string;
  roleName: string;
  roleDescription?: string | null;
  status?: string | null;
  securityPermission?: {
    name?: string | null;
    view?: boolean | null;
    create?: boolean | null;
    update?: boolean | null;
    delete?: boolean | null;
  }[] | null;
  generalSetting?: {
    name?: string | null;
    view?: boolean | null;
    create?: boolean | null;
    update?: boolean | null;
    delete?: boolean | null;
  }[] | null;
  manage?: {
    name?: string | null;
    view?: boolean | null;
    create?: boolean | null;
    update?: boolean | null;
    delete?: boolean | null;
  }[] | null;
  transaction?: {
    transaction_deposit_view?: boolean | null;
    transaction_deposit_update?: boolean | null;
    transaction_deposit_delete?: boolean | null;
    transaction_withdraw_view?: boolean | null;
    transaction_withdraw_update?: boolean | null;
    transaction_withdraw_delete?: boolean | null;
    transaction_payment_gateway_view?: boolean | null;
    transaction_payment_gateway_update?: boolean | null;
    transaction_payment_gateway_delete?: boolean | null;
    tranasction_payment_gateway_view?: boolean | null;
    tranasction_payment_gateway_update?: boolean | null;
    tranasction_payment_gateway_delete?: boolean | null;
    min_transaction_deposit?: number | null;
    max_transaction_deposit?: number | null;
    min_transaction_withdraw?: number | null;
    max_transaction_withdraw?: number | null;
    min_transaction_payment_gateway?: number | null;
    max_transaction_payment_gateway?: number | null;
  } | null;
  createdAt?: string;
  updatedAt?: string;
}

interface AuthState {
  token: string | null;
  user: User | null;
  role: UserRole | null;
  roleHydratedAt: string | null;
  isAuthenticated: boolean;
  loginTimestamp: number | null;
  ipAddress: string | null;
  deviceId: string | null;
  hasHydrated: boolean;

  // Actions
  setHasHydrated: (hasHydrated: boolean) => void;
  setAuth: (token: string, user: User, role?: UserRole | null) => void;
  clearAuth: () => void;
  updateUser: (user: Partial<User>) => void;
  setRole: (role: UserRole | null) => void;
  setSelectedCurrency: (
    currency: string,
    credit?: number,
    limit?: number,
  ) => void;
  setDeviceInfo: (ipAddress: string, deviceId: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      role: null,
      roleHydratedAt: null,
      isAuthenticated: false,
      loginTimestamp: null,
      ipAddress: null,
      deviceId: null,
      hasHydrated: false,

      setHasHydrated: (hasHydrated: boolean) => {
        set({ hasHydrated });
      },

      setAuth: (token: string, user: User, role: UserRole | null = null) => {
        set({
          token,
          user,
          role,
          roleHydratedAt: role?.id ?? null,
          isAuthenticated: true,
          loginTimestamp: getSyncedTimeMs(),
        });
      },

      clearAuth: () => {
        set({
          token: null,
          user: null,
          role: null,
          roleHydratedAt: null,
          isAuthenticated: false,
          loginTimestamp: null,
          ipAddress: null,
          deviceId: null,
        });
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: { ...currentUser, ...userData },
          });
        }
      },
      setRole: (role: UserRole | null) => {
        set({ role, roleHydratedAt: role?.id ?? null });
      },
      setSelectedCurrency: (
        currency: string,
        credit?: number,
        limit?: number,
      ) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: {
              ...currentUser,
              selectedCurrency: currency,
              credit: credit ?? currentUser.credit,
              limit: limit ?? currentUser.limit,
            },
          });
        }
      },
      setDeviceInfo: (ipAddress: string, deviceId: string) => {
        set({ ipAddress, deviceId });
      },
    }),
    {
      name: 'auth-storage', // localStorage key
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        role: state.role,
        roleHydratedAt: state.roleHydratedAt,
        isAuthenticated: state.isAuthenticated,
        loginTimestamp: state.loginTimestamp,
        ipAddress: state.ipAddress,
        deviceId: state.deviceId,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    },
  ),
);
